import { useEffect, useMemo } from 'react';
import { useMatches, useParams } from 'react-router-dom';

import { formatReferenceViewID } from '../../modules/referenceView/index.ts';
import { setProperties } from '../../redux/features/referenceBarUISlice.ts';
import { useAppDispatch } from '../../redux/hooks.ts';
import {
  ReferenceBarPropertyMetadataByID,
  Tkv2OrderContext,
  Tkv2ReferenceBarUserInterface,
} from '../types.ts';

export const useNewTkv2ReferenceBarUserInterface = (): Tkv2ReferenceBarUserInterface => {
  const dispatch = useAppDispatch();
  // Whenever we switch between order subroutes (paired sales & paired cost),
  // clear out the properties (it's confusing if selected comp sticks around
  // for paired sales, or if sales pairs stick around for paired cost)
  const matches = useMatches() ?? [];
  const inPairedSales = matches.filter((match) => match.pathname.endsWith('studies')).length > 0;
  const { studyID } = useParams();
  useEffect(() => {
    dispatch(setProperties([]));
  }, [dispatch, inPairedSales, studyID]);

  return useMemo(
    () => ({
      inPairedSales,
    }),
    [inPairedSales],
  );
};

export function getTkv2MetadataByID(
  orderContext: Tkv2OrderContext | null,
): ReferenceBarPropertyMetadataByID {
  if (!orderContext) {
    return {};
  }

  const subject = orderContext.subject.body;
  const comps = orderContext.comps;

  const compsMap = Object.fromEntries(
    Object.entries(comps).map(([id, comp]) => [
      formatReferenceViewID({ type: 'comparable', compID: id }),
      {
        listingID: comp.getStructuredData('toolkitListingID'),
        address: comp.getStructuredData('address'),
      },
    ]),
  );

  return {
    [formatReferenceViewID({ type: 'subject' })]: {
      listingID: subject.getStructuredData('toolkitListingID'),
      address: subject.getStructuredData('address'),
    },
    ...compsMap,
  };
}
